import React, { FC, useCallback } from 'react';
import styles from './CaptionSettingsPanel.module.scss';
import { CaptionSettings } from '@/types/project';

// Type definitions
export type OptionMode = 'default' | 'customize';
export type AnimationSpeed = '0.5x' | '1x' | '2x' | 'Instant';
export type FontOption =
  | 'Montserrat'
  | 'Open Sans Light'
  | 'Open Sans Regular'
  | 'Open Sans Medium'
  | 'Open Sans SemiBold'
  | 'Open Sans Bold'
  | 'Open Sans ExtraBold';
export type BorderWidth = '4px' | '6px' | '8px';

// LocalStorage key for Master Template
export const MASTER_TEMPLATE_KEY = 'captionMasterTemplateSettings';

// Map font option names to actual CSS font-family values
export const fontFamilyMap: Record<FontOption, string> = {
  Montserrat: "'Montserrat', sans-serif",
  'Open Sans Light': "'Open Sans', sans-serif",
  'Open Sans Regular': "'Open Sans', sans-serif",
  'Open Sans Medium': "'Open Sans', sans-serif",
  'Open Sans SemiBold': "'Open Sans', sans-serif",
  'Open Sans Bold': "'Open Sans', sans-serif",
  'Open Sans ExtraBold': "'Open Sans', sans-serif",
};

export const fontWeightMap: Record<FontOption, number> = {
  Montserrat: 400,
  'Open Sans Light': 300,
  'Open Sans Regular': 400,
  'Open Sans Medium': 500,
  'Open Sans SemiBold': 600,
  'Open Sans Bold': 700,
  'Open Sans ExtraBold': 800,
};

// Default caption settings
export const defaultCaptionSettings: CaptionSettings = {
  enabled: false,
  optionMode: 'default',
  animationSpeed: 'Instant',
  captionFont: 'Montserrat',
  captionColor: '#FFFFFF',
  borderEnabled: false,
  borderColor: '#000000',
  borderWidth: '8px',
  highlightEnabled: false,
  highlightColor: '#FFFF00',
  highlightWordColor: '#FF0000',
  adaptiveContrast: false,
};

// Helper function to load Master Template settings from localStorage
export const loadMasterTemplateSettings = (): Partial<CaptionSettings> | null => {
  try {
    const saved = localStorage.getItem(MASTER_TEMPLATE_KEY);
    if (saved) {
      return JSON.parse(saved) as Partial<CaptionSettings>;
    }
  } catch (e) {
    console.error('Error loading Master Template settings:', e);
  }
  return null;
};

// Helper function to save Master Template settings to localStorage
export const saveMasterTemplateSettings = (settings: Partial<CaptionSettings>): void => {
  try {
    localStorage.setItem(MASTER_TEMPLATE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving Master Template settings:', e);
  }
};

// Helper function to get effective caption settings (respecting default vs customize mode)
export const getEffectiveCaptionSettings = (settings: CaptionSettings): CaptionSettings => {
  if (settings.optionMode === 'default') {
    return {
      ...settings,
      captionFont: 'Montserrat',
      captionColor: '#FFFFFF',
      borderEnabled: false,
      borderColor: '#000000',
      borderWidth: '8px',
      highlightEnabled: false,
      highlightColor: '#FFFF00',
      highlightWordColor: '#FF0000',
      adaptiveContrast: false,
    };
  }
  return settings;
};

// Generate text shadow for border effect
export const getCaptionTextShadow = (settings: CaptionSettings): string => {
  if (!settings.borderEnabled) return 'none';

  const width = parseInt(settings.borderWidth, 10) / 2;
  const color = settings.borderColor;

  const shadows = [];
  for (let x = -width; x <= width; x++) {
    for (let y = -width; y <= width; y++) {
      if (x !== 0 || y !== 0) {
        shadows.push(`${x}px ${y}px 0 ${color}`);
      }
    }
  }
  return shadows.join(', ');
};

// Get CSS font-family value for a font option
export const getCaptionFontFamily = (font: FontOption): string => {
  return fontFamilyMap[font] || fontFamilyMap['Montserrat'];
};

// Get CSS font-weight value for a font option
export const getCaptionFontWeight = (font: FontOption): number => {
  return fontWeightMap[font] || fontWeightMap['Montserrat'];
};

interface CaptionSettingsPanelProps {
  settings: CaptionSettings;
  onChange(settings: CaptionSettings): void;
}

const animationSpeeds: AnimationSpeed[] = ['0.5x', '1x', '2x', 'Instant'];
const fonts: FontOption[] = [
  'Montserrat',
  'Open Sans Light',
  'Open Sans Regular',
  'Open Sans Medium',
  'Open Sans SemiBold',
  'Open Sans Bold',
  'Open Sans ExtraBold',
];
const widthOptions: BorderWidth[] = ['4px', '6px', '8px'];

const CaptionSettingsPanel: FC<CaptionSettingsPanelProps> = ({ settings, onChange }) => {
  const updateSettings = useCallback(
    (updates: Partial<CaptionSettings>) => {
      // Handle mutual exclusivity between adaptive contrast and border
      if (updates.adaptiveContrast === true) {
        onChange({ ...settings, ...updates, borderEnabled: false });
      } else if (updates.borderEnabled === true) {
        onChange({ ...settings, ...updates, adaptiveContrast: false });
      } else {
        onChange({ ...settings, ...updates });
      }
    },
    [settings, onChange],
  );

  const getSpeedPosition = (speed: AnimationSpeed): number => {
    const index = animationSpeeds.indexOf(speed);
    return (index / (animationSpeeds.length - 1)) * 100;
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);
    const index = Math.round((value / 100) * (animationSpeeds.length - 1));
    updateSettings({ animationSpeed: animationSpeeds[index] });
  };

  return (
    <div className={styles.captionSettingsPanel}>
      {/* Option Mode Buttons */}
      <div className={styles.optionModeButtons}>
        <button
          className={`${styles.modeButton} ${
            settings.optionMode === 'default' ? styles.active : ''
          }`}
          onClick={() => updateSettings({ optionMode: 'default' })}
        >
          Use Default Options
        </button>
        <button
          className={`${styles.modeButton} ${
            settings.optionMode === 'customize' ? styles.active : ''
          }`}
          onClick={() => updateSettings({ optionMode: 'customize' })}
        >
          Customize Options
        </button>
      </div>

      {/* Customize Options - Only visible when "Customize Options" is selected */}
      {settings.optionMode === 'customize' && (
        <div className={styles.customizeOptions}>
          {/* Animation Speed */}
          <div className={styles.optionGroup}>
            <label className={styles.optionLabel}>
              Caption animation speed
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>
                  Controls the speed at which captions appear on screen
                </span>
              </span>
            </label>
            <div className={styles.speedSlider}>
              <input
                type="range"
                min="0"
                max="100"
                value={getSpeedPosition(settings.animationSpeed as AnimationSpeed)}
                onChange={handleSpeedChange}
                className={styles.slider}
              />
              <div className={styles.speedMarks}>
                {animationSpeeds.map((speed) => (
                  <span
                    key={speed}
                    className={`${styles.speedMark} ${
                      settings.animationSpeed === speed ? styles.activeMark : ''
                    }`}
                  >
                    {speed}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Caption Font */}
          <div className={styles.optionGroup}>
            <label className={styles.optionLabel}>
              Caption font
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>Choose the font style for your captions</span>
              </span>
            </label>
            <select
              value={settings.captionFont}
              onChange={(e) => updateSettings({ captionFont: e.target.value as FontOption })}
              className={styles.select}
            >
              {fonts.map((font) => (
                <option key={font} value={font}>
                  {font}
                </option>
              ))}
            </select>
          </div>

          {/* Caption Color */}
          <div className={styles.optionGroup}>
            <label className={styles.optionLabel}>
              Caption color
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>Set the text color for your captions</span>
              </span>
            </label>
            <div className={styles.colorPicker}>
              <input
                type="color"
                value={settings.captionColor}
                onChange={(e) => updateSettings({ captionColor: e.target.value })}
                className={styles.colorInput}
              />
              <input
                type="text"
                value={settings.captionColor}
                onChange={(e) => updateSettings({ captionColor: e.target.value })}
                className={styles.colorText}
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          {/* Caption Border */}
          <div
            className={`${styles.optionGroup} ${settings.adaptiveContrast ? styles.disabled : ''}`}
          >
            <label className={styles.optionLabel}>
              Caption border
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>
                  Add an outline border around caption text for better visibility
                  {settings.adaptiveContrast && ' (Disabled when Adaptive Contrast is enabled)'}
                </span>
              </span>
            </label>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.borderEnabled}
                  onChange={(e) => updateSettings({ borderEnabled: e.target.checked })}
                  disabled={settings.adaptiveContrast}
                />
                <span>Enable border</span>
              </label>
            </div>
            <div
              className={`${styles.subOptions} ${!settings.borderEnabled ? styles.disabled : ''}`}
            >
              <div className={styles.subOption}>
                <label>Border color</label>
                <div className={styles.colorPicker}>
                  <input
                    type="color"
                    value={settings.borderColor}
                    onChange={(e) => updateSettings({ borderColor: e.target.value })}
                    className={styles.colorInput}
                    disabled={!settings.borderEnabled}
                  />
                  <input
                    type="text"
                    value={settings.borderColor}
                    onChange={(e) => updateSettings({ borderColor: e.target.value })}
                    className={styles.colorText}
                    placeholder="#000000"
                    disabled={!settings.borderEnabled}
                  />
                </div>
              </div>
              <div className={styles.subOption}>
                <label>Border width</label>
                <select
                  value={settings.borderWidth}
                  onChange={(e) => updateSettings({ borderWidth: e.target.value as BorderWidth })}
                  className={styles.selectCompact}
                  disabled={!settings.borderEnabled}
                >
                  {widthOptions.map((width) => (
                    <option key={width} value={width}>
                      {width}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Caption Highlight */}
          <div className={styles.optionGroup}>
            <label className={styles.optionLabel}>
              Caption highlight
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>
                  Highlight specific words or phrases in your captions
                </span>
              </span>
            </label>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.highlightEnabled}
                  onChange={(e) => updateSettings({ highlightEnabled: e.target.checked })}
                />
                <span>Enable highlight</span>
              </label>
            </div>
            <div
              className={`${styles.subOptions} ${
                !settings.highlightEnabled ? styles.disabled : ''
              }`}
            >
              <div className={styles.subOption}>
                <label>Highlight color</label>
                <div className={styles.colorPicker}>
                  <input
                    type="color"
                    value={settings.highlightColor}
                    onChange={(e) => updateSettings({ highlightColor: e.target.value })}
                    className={styles.colorInput}
                    disabled={!settings.highlightEnabled}
                  />
                  <input
                    type="text"
                    value={settings.highlightColor}
                    onChange={(e) => updateSettings({ highlightColor: e.target.value })}
                    className={styles.colorText}
                    placeholder="#FFFF00"
                    disabled={!settings.highlightEnabled}
                  />
                </div>
              </div>
              <div className={styles.subOption}>
                <label>Highlight word</label>
                <div className={styles.colorPicker}>
                  <input
                    type="color"
                    value={settings.highlightWordColor}
                    onChange={(e) => updateSettings({ highlightWordColor: e.target.value })}
                    className={styles.colorInput}
                    disabled={!settings.highlightEnabled}
                  />
                  <input
                    type="text"
                    value={settings.highlightWordColor}
                    onChange={(e) => updateSettings({ highlightWordColor: e.target.value })}
                    className={styles.colorText}
                    placeholder="#FF0000"
                    disabled={!settings.highlightEnabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Adaptive Contrast */}
          <div className={styles.optionGroup}>
            <label className={styles.optionLabel}>
              Adaptive contrast
              <span className={styles.tooltipWrapper}>
                <span className={styles.questionMark}>?</span>
                <span className={styles.tooltip}>
                  Automatically adjusts border color (black/white) based on video brightness. Border
                  width is fixed at 8px when enabled.
                  {settings.borderEnabled && ' (Enabling this will disable Caption Border)'}
                </span>
              </span>
            </label>
            <div className={styles.toggleSwitch}>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.adaptiveContrast}
                  onChange={(e) => updateSettings({ adaptiveContrast: e.target.checked })}
                />
                <span className={styles.sliderRound}></span>
              </label>
              <span className={styles.toggleLabel}>{settings.adaptiveContrast ? 'On' : 'Off'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaptionSettingsPanel;
